import { useState, useEffect } from 'react';
import { getStatisticsWithComparison } from '../services/productService';
import { MonthlySnapshot } from '../types';

interface UseProductStatisticsResult {
  statistics: {
    totalProducts: number;
    lowStockProducts: number;
    outOfStockProducts: number;
    totalInventoryValue: number;
  } | null;
  previousSnapshot: MonthlySnapshot | null;
  percentageChanges: {
    totalProducts: number;
    lowStockProducts: number;
    outOfStockProducts: number;
    totalInventoryValue: number;
  } | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export const useProductStatistics = (): UseProductStatisticsResult => {
  const [statistics, setStatistics] = useState<{
    totalProducts: number;
    lowStockProducts: number;
    outOfStockProducts: number;
    totalInventoryValue: number;
  } | null>(null);
  const [previousSnapshot, setPreviousSnapshot] = useState<MonthlySnapshot | null>(null);
  const [percentageChanges, setPercentageChanges] = useState<{
    totalProducts: number;
    lowStockProducts: number;
    outOfStockProducts: number;
    totalInventoryValue: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getStatisticsWithComparison();
      setStatistics(result.current);
      setPreviousSnapshot(result.previous);
      setPercentageChanges(result.percentageChanges);
    } catch (err) {
      console.error('Error fetching product statistics:', err);
      setError('Failed to load product statistics');
      setStatistics(null);
      setPreviousSnapshot(null);
      setPercentageChanges(null);
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    await fetchStatistics();
  };

  useEffect(() => {
    fetchStatistics();
  }, []);

  return {
    statistics,
    previousSnapshot,
    percentageChanges,
    loading,
    error,
    refresh
  };
}; 